import { Plus } from 'lucide-react';
import React, { useState } from 'react';

import { AddProviderModal } from '@/components/provider-directory/AddProviderModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import {
  ScreeningProvider,
  ScreeningProviderRow,
} from './ScreeningProviderRow';
import { ScreeningToggle } from './ScreeningToggle';
import {
  ScreeningAnswer,
  ScreeningInputField,
  ScreeningMainQuestion,
  ScreeningResponseOption,
  ScreeningResponseType,
  ScreeningSubQuestion,
  ScreeningTextFields,
} from './types';

// ---------------------------------------------------------------------------
// Input Field Renderer
// ---------------------------------------------------------------------------

interface InputFieldRendererProps {
  field: ScreeningInputField;
  questionId: string;
  value: string;
  onChange: (value: string) => void;
  isHighlighted?: boolean;
}

const InputFieldRenderer: React.FC<InputFieldRendererProps> = ({
  field,
  questionId,
  value,
  onChange,
  isHighlighted = false,
}) => {
  const baseClassName = cn(
    'h-7 text-xs',
    isHighlighted && !value && 'border-zinc-400',
  );

  switch (field.fieldType) {
    case 'dropdown':
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className={cn(baseClassName, 'min-w-[120px]')}>
            <SelectValue placeholder={field.placeholder || field.label} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'multi_select':
      // For now, render as dropdown (multi-select would need more complex state)
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className={cn(baseClassName, 'min-w-[120px]')}>
            <SelectValue placeholder={field.placeholder || field.label} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'textarea':
      return (
        <Input
          placeholder={field.placeholder || field.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={baseClassName}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          placeholder={field.placeholder || field.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(baseClassName, 'w-24')}
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          placeholder={field.placeholder || field.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(baseClassName, 'w-32')}
        />
      );
    case 'text':
    default:
      return (
        <Input
          placeholder={field.placeholder || field.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={baseClassName}
        />
      );
  }
};

// ---------------------------------------------------------------------------
// Screening Question Components
// ---------------------------------------------------------------------------

interface ScreeningQuestionProps {
  questionId: string;
  text: string;
  answer: ScreeningAnswer;
  onAnswerChange: (answer: ScreeningAnswer) => void;
  isSubQuestion?: boolean;
  binaryOnly?: boolean;
  dimmed?: boolean;
  /** Show pulsing indicator to prompt user attention */
  needsAttention?: boolean;
  /** How this question should be answered (toggle, text, dropdown, etc.) */
  responseType?: ScreeningResponseType;
  /** Options for dropdown/multi_select response types */
  responseOptions?: ScreeningResponseOption[];
}

export const ScreeningQuestion: React.FC<ScreeningQuestionProps> = ({
  questionId: _questionId,
  text,
  answer,
  onAnswerChange,
  isSubQuestion = false,
  binaryOnly = false,
  dimmed = false,
  needsAttention = false,
  responseType = 'toggle',
  responseOptions = [],
}) => {
  // Render the appropriate input based on response type
  const renderResponseInput = () => {
    const baseClassName = cn(
      isSubQuestion ? 'h-6 text-[11px]' : 'h-7 text-xs',
      needsAttention && !dimmed && 'border-zinc-400',
    );

    switch (responseType) {
      case 'text':
        return (
          <Input
            placeholder="Enter response..."
            value={answer === 'unknown' ? '' : answer}
            onChange={(e) => onAnswerChange(e.target.value || 'unknown')}
            className={cn(baseClassName, 'min-w-[120px] max-w-[200px]')}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            placeholder="0"
            value={answer === 'unknown' ? '' : answer}
            onChange={(e) => onAnswerChange(e.target.value || 'unknown')}
            className={cn(baseClassName, 'w-20')}
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={answer === 'unknown' ? '' : answer}
            onChange={(e) => onAnswerChange(e.target.value || 'unknown')}
            className={cn(baseClassName, 'w-32')}
          />
        );
      case 'dropdown':
        return (
          <Select
            value={answer === 'unknown' ? '' : answer}
            onValueChange={(value) => onAnswerChange(value || 'unknown')}
          >
            <SelectTrigger
              className={cn(baseClassName, 'min-w-[120px] max-w-[180px]')}
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {responseOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multi_select':
        // For now, render as single dropdown (proper multi-select would need checkbox list)
        return (
          <Select
            value={answer === 'unknown' ? '' : answer}
            onValueChange={(value) => onAnswerChange(value || 'unknown')}
          >
            <SelectTrigger
              className={cn(baseClassName, 'min-w-[120px] max-w-[180px]')}
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {responseOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'toggle':
      default:
        return (
          <ScreeningToggle
            value={answer}
            onChange={onAnswerChange}
            binaryOnly={binaryOnly || isSubQuestion}
            compact={isSubQuestion}
            needsAttention={needsAttention && !dimmed}
          />
        );
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2',
        isSubQuestion ? 'py-0.5 pl-4' : 'py-1',
        dimmed && 'opacity-40 pointer-events-none',
      )}
    >
      <div className="flex items-center gap-1 min-w-0 flex-1">
        {isSubQuestion && <span className="text-zinc-300 text-[10px]">└</span>}
        <span
          className={cn(
            isSubQuestion
              ? 'text-[11px] text-zinc-500'
              : 'text-xs font-medium text-zinc-800',
          )}
        >
          {text}
        </span>
      </div>
      {renderResponseInput()}
    </div>
  );
};

interface ScreeningCategoryCardProps {
  categoryId: string;
  letter: string;
  mainQuestion: ScreeningMainQuestion;
  mainAnswer: ScreeningAnswer;
  onMainAnswerChange: (answer: ScreeningAnswer) => void;
  subQuestions?: ScreeningSubQuestion[];
  /** Single backup question (legacy) */
  backupQuestion?: ScreeningSubQuestion;
  /** Multiple backup questions (e.g., Coverage B) */
  backupQuestions?: ScreeningSubQuestion[];
  getAnswer: (questionId: string) => ScreeningAnswer;
  onAnswerChange: (questionId: string, answer: ScreeningAnswer) => void;
  showSubQuestions: boolean;
  showBackupQuestion: boolean;
  /** Text field data for sub-questions */
  textFields?: ScreeningTextFields;
  /** Callback for text field changes */
  onTextFieldChange?: (
    questionId: string,
    fieldLabel: string,
    value: string,
  ) => void;
  /** Track which questions have been explicitly interacted with */
  touchedQuestions?: Set<string>;
  /** Whether header fields are complete (for progressive pulsing) */
  isHeaderComplete?: boolean;
  /** Provider services added during screening (for treatment question) */
  providers?: ScreeningProvider[];
  /** Callback when a provider is added */
  onProviderAdd?: (id: string, name: string) => void;
  /** Callback when a provider is removed */
  onProviderRemove?: (id: string) => void;
}

export const ScreeningCategoryCard: React.FC<ScreeningCategoryCardProps> = ({
  categoryId,
  letter,
  mainQuestion,
  mainAnswer,
  onMainAnswerChange,
  subQuestions = [],
  backupQuestion,
  backupQuestions = [],
  getAnswer,
  onAnswerChange,
  showSubQuestions,
  showBackupQuestion,
  textFields = {},
  onTextFieldChange,
  touchedQuestions = new Set(),
  isHeaderComplete = false,
  providers = [],
  onProviderAdd,
  onProviderRemove,
}) => {
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);

  const isTreatmentCategory = categoryId === 'treatment';
  const showProviderSection =
    isTreatmentCategory && mainAnswer === 'yes' && onProviderAdd;

  const handleProviderCreated = (id: string, name?: string) => {
    if (onProviderAdd && name) {
      onProviderAdd(id, name);
    }
    setIsProviderModalOpen(false);
  };
  // Combine single backup question with multiple backup questions
  const allBackupQuestions = backupQuestion
    ? [backupQuestion, ...backupQuestions]
    : backupQuestions;

  // Always show all questions - dim the ones that don't apply
  const hasSubQuestions = subQuestions.length > 0;
  const hasBackupQuestions = allBackupQuestions.length > 0;

  // Determine if a question needs attention (pulsing indicator)
  // Parent questions: NEVER pulse - we only pulse child questions after parent is answered
  // This creates a guided flow where users focus on parent questions first
  const mainQuestionNeedsAttention = false;

  // Sub-questions need attention when:
  // 1. Header fields are complete (progressive disclosure)
  // 2. Parent is answered yes/no (showSubQuestions is true)
  // 3. This sub-question hasn't been touched
  // 4. The answer is still 'unknown' (default)
  const subQuestionNeedsAttention = (questionId: string) => {
    if (!isHeaderComplete) return false;
    if (!showSubQuestions) return false;
    return (
      !touchedQuestions.has(questionId) && getAnswer(questionId) === 'unknown'
    );
  };

  // Backup questions need attention when:
  // 1. Header fields are complete (progressive disclosure)
  // 2. Parent answer triggers backup (showBackupQuestion is true)
  // 3. This backup question hasn't been touched
  // 4. The answer is still 'unknown' (default)
  const backupQuestionNeedsAttention = (questionId: string) => {
    if (!isHeaderComplete) return false;
    if (!showBackupQuestion) return false;
    return (
      !touchedQuestions.has(questionId) && getAnswer(questionId) === 'unknown'
    );
  };

  return (
    <div className="rounded border bg-white border-zinc-200">
      {/* Main question row */}
      <div className="flex items-center gap-2 px-2 py-1.5">
        {/* Letter badge */}
        <div className="flex h-6 w-6 items-center justify-center rounded text-[11px] font-bold text-white flex-shrink-0 bg-zinc-500">
          {letter}
        </div>

        {/* Question text and toggle */}
        <div className="flex-1 min-w-0">
          <ScreeningQuestion
            questionId={mainQuestion.id}
            text={mainQuestion.text}
            answer={mainAnswer}
            onAnswerChange={onMainAnswerChange}
            needsAttention={mainQuestionNeedsAttention}
            responseType={mainQuestion.responseType}
            responseOptions={mainQuestion.responseOptions}
          />
        </div>
      </div>

      {/* Main question text field(s) - shown based on configuration */}
      {mainQuestion.hasTextField &&
        (mainQuestion.alwaysShowTextFields ||
          (mainQuestion.showTextFieldOnAnswer
            ? mainAnswer === mainQuestion.showTextFieldOnAnswer
            : mainAnswer === 'yes')) && (
          <div className="px-2 pb-2">
            {/* Multiple text input fields mode (e.g., 3P/1P insurance fields) */}
            {mainQuestion.textFieldLabels &&
            mainQuestion.textFieldLabels.length > 0
              ? (() => {
                  // Check if fields are 3P/1P grouped (insurance fields)
                  const has3PFields = mainQuestion.textFieldLabels!.some((l) =>
                    l.startsWith('3P'),
                  );
                  const has1PFields = mainQuestion.textFieldLabels!.some((l) =>
                    l.startsWith('1P'),
                  );

                  if (has3PFields || has1PFields) {
                    // Grouped layout for 3P/1P insurance fields
                    const fields3P = mainQuestion.textFieldLabels!.filter((l) =>
                      l.startsWith('3P'),
                    );
                    const fields1P = mainQuestion.textFieldLabels!.filter((l) =>
                      l.startsWith('1P'),
                    );

                    return (
                      <div className="space-y-1.5">
                        {/* 3P Row */}
                        {fields3P.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-zinc-500 w-6 flex-shrink-0">
                              3P:
                            </span>
                            <div className="flex gap-1.5 flex-1">
                              {fields3P.map((label) => {
                                const isEmpty =
                                  !textFields[mainQuestion.id]?.[label];
                                return (
                                  <Input
                                    key={`${mainQuestion.id}-${label}`}
                                    placeholder={label.replace('3P ', '')}
                                    value={
                                      textFields[mainQuestion.id]?.[label] || ''
                                    }
                                    onChange={(e) =>
                                      onTextFieldChange?.(
                                        mainQuestion.id,
                                        label,
                                        e.target.value,
                                      )
                                    }
                                    className={cn(
                                      'h-6 text-[11px] flex-1 min-w-0',
                                      isHeaderComplete &&
                                        isEmpty &&
                                        'border-zinc-400',
                                    )}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* 1P Row */}
                        {fields1P.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-zinc-500 w-6 flex-shrink-0">
                              1P:
                            </span>
                            <div className="flex gap-1.5 flex-1">
                              {fields1P.map((label) => {
                                const isEmpty =
                                  !textFields[mainQuestion.id]?.[label];
                                return (
                                  <Input
                                    key={`${mainQuestion.id}-${label}`}
                                    placeholder={label.replace('1P ', '')}
                                    value={
                                      textFields[mainQuestion.id]?.[label] || ''
                                    }
                                    onChange={(e) =>
                                      onTextFieldChange?.(
                                        mainQuestion.id,
                                        label,
                                        e.target.value,
                                      )
                                    }
                                    className={cn(
                                      'h-6 text-[11px] flex-1 min-w-0',
                                      isHeaderComplete &&
                                        isEmpty &&
                                        'border-zinc-400',
                                    )}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Default: simple flex row for non-grouped fields
                  return (
                    <div className="flex gap-2">
                      {mainQuestion.textFieldLabels!.map((label) => (
                        <Input
                          key={`${mainQuestion.id}-${label}`}
                          placeholder={label}
                          value={textFields[mainQuestion.id]?.[label] || ''}
                          onChange={(e) =>
                            onTextFieldChange?.(
                              mainQuestion.id,
                              label,
                              e.target.value,
                            )
                          }
                          className="h-7 text-xs flex-1"
                        />
                      ))}
                    </div>
                  );
                })()
              : /* Single text field mode (e.g., Medical Providers, Previous Firm) */
                mainQuestion.textFieldLabel && (
                  <Input
                    placeholder={
                      mainQuestion.textFieldPlaceholder || 'Enter details...'
                    }
                    value={
                      textFields[mainQuestion.id]?.[
                        mainQuestion.textFieldLabel
                      ] || ''
                    }
                    onChange={(e) =>
                      onTextFieldChange?.(
                        mainQuestion.id,
                        mainQuestion.textFieldLabel!,
                        e.target.value,
                      )
                    }
                    className={cn(
                      'h-7 text-xs',
                      isHeaderComplete &&
                        !textFields[mainQuestion.id]?.[
                          mainQuestion.textFieldLabel
                        ] &&
                        'border-zinc-400',
                    )}
                  />
                )}
          </div>
        )}

      {/* Input fields (including dropdowns) from inputFields config */}
      {mainQuestion.inputFields && mainQuestion.inputFields.length > 0 && (
        <div className="px-2 pb-2">
          <div className="flex flex-wrap gap-2">
            {mainQuestion.inputFields
              .filter((field) => {
                // Show field based on showWhenAnswer config
                if (field.showWhenAnswer === 'always') return true;
                return field.showWhenAnswer === mainAnswer;
              })
              .map((field) => (
                <div key={field.key} className="flex-1 min-w-[120px]">
                  <InputFieldRenderer
                    field={field}
                    questionId={mainQuestion.id}
                    value={textFields[mainQuestion.id]?.[field.label] || ''}
                    onChange={(value) =>
                      onTextFieldChange?.(mainQuestion.id, field.label, value)
                    }
                    isHighlighted={isHeaderComplete}
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Provider section for treatment question */}
      {showProviderSection && (
        <div className="px-2 pb-2 space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
          {/* Added providers list */}
          {providers.length > 0 && (
            <div className="space-y-1.5">
              {providers.map((provider) => (
                <ScreeningProviderRow
                  key={provider.id}
                  provider={provider}
                  onRemove={onProviderRemove || (() => {})}
                />
              ))}
            </div>
          )}

          {/* Add provider button */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1.5 text-zinc-600 hover:text-zinc-800"
            onClick={() => setIsProviderModalOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Provider
          </Button>

          {/* Provider modal */}
          <AddProviderModal
            isOpen={isProviderModalOpen}
            onClose={() => setIsProviderModalOpen(false)}
            onProviderCreated={handleProviderCreated}
          />
        </div>
      )}

      {/* Sub-questions */}
      {hasSubQuestions && (
        <div className="border-t border-zinc-50 px-2 pb-1">
          {subQuestions.map((subQ) => (
            <React.Fragment key={subQ.id}>
              <ScreeningQuestion
                questionId={subQ.id}
                text={subQ.text}
                answer={getAnswer(subQ.id)}
                onAnswerChange={(answer) => onAnswerChange(subQ.id, answer)}
                isSubQuestion
                binaryOnly={
                  subQ.responseType === 'toggle' || !subQ.responseType
                }
                dimmed={!showSubQuestions}
                needsAttention={subQuestionNeedsAttention(subQ.id)}
                responseType={subQ.responseType}
                responseOptions={subQ.responseOptions}
              />
              {/* Text fields for sub-question (shown when answered Yes) */}
              {subQ.hasTextFields &&
                subQ.textFieldLabels &&
                getAnswer(subQ.id) === 'yes' &&
                showSubQuestions && (
                  <div className="pl-8 pb-1 space-y-1">
                    {subQ.textFieldLabels.map((label) => (
                      <Input
                        key={`${subQ.id}-${label}`}
                        placeholder={label}
                        value={textFields[subQ.id]?.[label] || ''}
                        onChange={(e) =>
                          onTextFieldChange?.(subQ.id, label, e.target.value)
                        }
                        className={cn(
                          'h-6 text-[11px] px-2',
                          isHeaderComplete &&
                            !textFields[subQ.id]?.[label] &&
                            'border-zinc-400',
                        )}
                      />
                    ))}
                  </div>
                )}
              {/* Input fields (including dropdowns) for sub-question */}
              {subQ.inputFields &&
                subQ.inputFields.length > 0 &&
                showSubQuestions && (
                  <div className="pl-8 pb-1">
                    <div className="flex flex-wrap gap-1.5">
                      {subQ.inputFields
                        .filter((field) => {
                          const answer = getAnswer(subQ.id);
                          if (field.showWhenAnswer === 'always') return true;
                          return field.showWhenAnswer === answer;
                        })
                        .map((field) => (
                          <div key={field.key} className="min-w-[100px]">
                            <InputFieldRenderer
                              field={field}
                              questionId={subQ.id}
                              value={textFields[subQ.id]?.[field.label] || ''}
                              onChange={(value) =>
                                onTextFieldChange?.(subQ.id, field.label, value)
                              }
                              isHighlighted={isHeaderComplete}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Backup questions */}
      {hasBackupQuestions && (
        <div
          className={cn(
            'border-t border-zinc-50 px-2 pb-1',
            hasSubQuestions && 'border-t-0',
          )}
        >
          {allBackupQuestions.map((bq) => (
            <React.Fragment key={bq.id}>
              <ScreeningQuestion
                questionId={bq.id}
                text={bq.text}
                answer={getAnswer(bq.id)}
                onAnswerChange={(answer) => onAnswerChange(bq.id, answer)}
                isSubQuestion
                binaryOnly={bq.responseType === 'toggle' || !bq.responseType}
                dimmed={!showBackupQuestion}
                needsAttention={backupQuestionNeedsAttention(bq.id)}
                responseType={bq.responseType}
                responseOptions={bq.responseOptions}
              />
              {/* Text fields for backup question (shown when answered Yes) */}
              {bq.hasTextFields &&
                bq.textFieldLabels &&
                getAnswer(bq.id) === 'yes' &&
                showBackupQuestion && (
                  <div className="pl-8 pb-1 space-y-1">
                    {bq.textFieldLabels.map((label) => (
                      <Input
                        key={`${bq.id}-${label}`}
                        placeholder={label}
                        value={textFields[bq.id]?.[label] || ''}
                        onChange={(e) =>
                          onTextFieldChange?.(bq.id, label, e.target.value)
                        }
                        className={cn(
                          'h-6 text-[11px] px-2',
                          isHeaderComplete &&
                            !textFields[bq.id]?.[label] &&
                            'border-zinc-400',
                        )}
                      />
                    ))}
                  </div>
                )}
              {/* Input fields (including dropdowns) for backup question */}
              {bq.inputFields &&
                bq.inputFields.length > 0 &&
                showBackupQuestion && (
                  <div className="pl-8 pb-1">
                    <div className="flex flex-wrap gap-1.5">
                      {bq.inputFields
                        .filter((field) => {
                          const answer = getAnswer(bq.id);
                          if (field.showWhenAnswer === 'always') return true;
                          return field.showWhenAnswer === answer;
                        })
                        .map((field) => (
                          <div key={field.key} className="min-w-[100px]">
                            <InputFieldRenderer
                              field={field}
                              questionId={bq.id}
                              value={textFields[bq.id]?.[field.label] || ''}
                              onChange={(value) =>
                                onTextFieldChange?.(bq.id, field.label, value)
                              }
                              isHighlighted={isHeaderComplete}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
